import { useNoteSaveStatus } from '@/features/tasks/hooks/useNoteSaveStatus';
import React, { useState } from "react";
import i18n from "@/shared/i18n/config";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { useTheme, ThemeProvider, cn } from "@/ui/theme/ThemeContext";
import {
  ProjectDataProvider,
  useProjectData,
} from "@/features/projects/ProjectDataContext";
import { MultiProjectProvider, useMultiProject } from "@/features/projects/MultiProjectContext";
import { getProjectCompanyLabel } from "@/features/projects/utils/projectCompany";
import { SettingsProvider } from "@/features/settings/contexts/SettingsContext";
import { DashboardLayoutProvider } from "@/features/dashboard/contexts/DashboardLayoutContext";
import { AuthProvider, useAuth } from "@/features/auth/AuthContext";
import type { User as AuthUser } from "@/features/auth/AuthContext";
import { ProtectedRoute } from "@/ui/components/navigation/ProtectedRoute";
import { LoginPage } from "@/features/auth/LoginPage";
import { SignupPage } from "@/features/auth/SignupPage";
import { BillingSetupPage } from "@/features/auth/BillingSetupPage";
import { LegalPolicyPage } from "@/features/legal/LegalPolicyPage";
import { PricingPage } from "@/features/legal/PricingPage";
import { useKeyboardShortcuts } from "@/shared/hooks/useKeyboardShortcuts";
import { useUndoShortcuts } from "@/shared/hooks";
import { useModalEscape } from "@/shared/services/KeyboardEventManager";
import { setAuditView, trackAuditEvent } from "@/shared/audit/auditClient";
import { useDeviceUxProfile } from "@/shared/device/useDeviceUxProfile";
import { embedRequested } from "@/features/saiEmbed/embedAccess";

// Extracted components and hooks
import { AppHeader } from "./components/AppHeader";
import { type InboxNotification } from "./components/MentionInboxPopover";
import { AppNavigation } from "./components/AppNavigation";
import { NoAccessibleWorkspace } from "./components/NoAccessibleWorkspace";
import {
  SelfServiceGuidedTour,
  type SelfServiceGuidedTourStepId,
} from "./components/SelfServiceGuidedTour";
import { SelfServiceSetupWizard } from "./components/SelfServiceSetupWizard";
import { SessionRecoveryScreen } from "./components/SessionRecoveryScreen";
import { useMenuManagement } from "./hooks/useMenuManagement";
import { resolveAccessibleActiveView } from "./utils/navigationAccess";
import { renderActiveView } from "./utils/viewComponentMapping";
import { getDocsOpenRequestFromLocation } from "./utils/docsOpenRequestFromLocation";
import { readOnboardingStatus, readOnboardingTemplate } from "./utils/onboardingSettings";

// UI components
import { SearchPanel } from "@/ui/components/SearchPanel";
import { ImportExportModal } from "@/ui/components/overlays/ImportExportModal";
import { TaskDetailsPanel } from "@/features/tasks/components/TaskDetailsPanel";
import { ProjectFilterDropdownHost } from "@/features/tasks/components/ProjectFilterDropdown";
import { AsyncErrorBoundary } from "@/shared/components";
import { ProjectNode } from "@/shared/types/projectData";
// Keep the Settings feature barrel behind its lazy view boundary. Importing this
// always-mounted panel through the barrel eagerly pulled SettingsView into the
// application entry chunk and defeated the route-level split.
import { UndoHistoryPanel } from "@/features/settings/components/UndoHistoryPanel";
import { FeedbackModeHost } from "@/features/feedbackMode";
import { useSettings } from "@/features/settings/contexts/SettingsContext";
import { closeAllDropdowns } from "@/ui/components/overlays/dropdownCoordinator";
import { PrintPreviewPage } from "@/features/editor/PrintPreviewPage";
import {
  dispatchDocsOpenDocumentRequest,
  type DocsOpenDocumentRequest,
  getPendingDocsOpenRequest,
} from "@/features/docs/utils/docsOpenRequest";
import { getNodeId, getProjectIdFromNodeId } from "@/features/projects/utils/projectUtils";
const SaiNativeConsent = React.lazy(() => import('@/features/saiNative/SaiNativeConsent').then(m => ({default:m.SaiNativeConsent})));
import { AgentOfficeEmbedPage } from "@/features/agentOffice";

const APP_ACTIVE_VIEW_STORAGE_PREFIX = "app-active-view";
const APP_ACTIVE_VIEW_QUERY_KEY = "view";
const APP_SETTINGS_TAB_QUERY_KEY = "settingsTab";
const APP_SEARCH_TRIGGER_ID = "app-search-trigger";
type PendingDocsOpenRequest = Omit<DocsOpenDocumentRequest, "requestedAt">;
type SettingsEntryTopTab = "general" | "users" | "projects" | "mcp-monitoring" | "audit-monitor" | "guidelines" | "feedback-mode" | "ai";
type NormalizedViewRequest = {
  view: string;
  settingsEntryTopTabHint: SettingsEntryTopTab | null;
};
const ROUTER_BASENAME = (() => {
  const rawBaseUrl = (import.meta.env.BASE_URL || "/").trim();
  const withoutSlashes = rawBaseUrl.replace(/^\/+|\/+$/g, "");
  return withoutSlashes ? `/${withoutSlashes}` : undefined;
})();
const MobilePlanningBoardPrototype = React.lazy(() =>
  import("@/features/mobile-planning/MobilePlanningBoardPrototype").then((m) => ({
    default: m.MobilePlanningBoardPrototype,
  })),
);

const normalizePendingDocsOpenRequest = (
  request: DocsOpenDocumentRequest | PendingDocsOpenRequest | null | undefined,
): PendingDocsOpenRequest | null => {
  if (!request?.nodeId) {
    return null;
  }

  const { requestedAt: _requestedAt, ...pendingRequest } = request as DocsOpenDocumentRequest;
  return pendingRequest;
};

const getInitialDocsOpenRequest = (): PendingDocsOpenRequest | null => (
  normalizePendingDocsOpenRequest(getDocsOpenRequestFromLocation()) ||
  normalizePendingDocsOpenRequest(getPendingDocsOpenRequest())
);

const getEffectiveUserScopeKey = (
  user: AuthUser | null,
  actualUser: AuthUser | null,
  viewAsUserId: string | null,
  isViewingAs: boolean,
): string => {
  if (isViewingAs && viewAsUserId) {
    return `view-as:${viewAsUserId}`;
  }
  return `self:${actualUser?.id || user?.id || "anonymous"}`;
};

const getActiveViewStorageKey = (scopeKey: string): string =>
  `${APP_ACTIVE_VIEW_STORAGE_PREFIX}:${scopeKey}`;

const getStoredActiveView = (scopeKey: string): string | null => {
  try {
    return localStorage.getItem(getActiveViewStorageKey(scopeKey));
  } catch {
    return null;
  }
};

const getActiveViewFromLocation = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  return params.get(APP_ACTIVE_VIEW_QUERY_KEY);
};

const normalizeRequestedView = (
  requestedView: string | null | undefined
): NormalizedViewRequest => {
  const view = requestedView || "dashboard";

  if (view === "admin") {
    return {
      view: "settings",
      settingsEntryTopTabHint: "users",
    };
  }

  return {
    view,
    settingsEntryTopTabHint: null,
  };
};

const writeActiveViewToHistory = (
  view: string,
  mode: "push" | "replace"
) => {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);

  if (view && view !== "dashboard") {
    url.searchParams.set(APP_ACTIVE_VIEW_QUERY_KEY, view);
  } else {
    url.searchParams.delete(APP_ACTIVE_VIEW_QUERY_KEY);
  }

  if (view !== "settings") {
    url.searchParams.delete(APP_SETTINGS_TAB_QUERY_KEY);
  }

  const nextState = {
    ...(window.history.state || {}),
    appView: view,
  };

  if (mode === "replace") {
    window.history.replaceState(nextState, "", url);
  } else {
    window.history.pushState(nextState, "", url);
  }
};

const AppContent: React.FC = () => {
  const { isDarkMode, toggleDarkMode, theme } = useTheme();
  // Stays true for the whole document once SAI Projects admitted it.
  const isSaiEmbedded = embedRequested();
  const navigate = useNavigate();
  const { projectData, captureOperation, syncState, hasUnsavedChanges: hasLocalChanges } = useProjectData();
  const { activeProjectId, selectProjects, visibleProjects, refreshProject, setActiveProject } = useMultiProject();
  const {
    user,
    actualUser,
    viewAsUserId,
    viewAsOptions,
    isViewingAs,
    setViewAsUserId,
    updateSelfServicePlan,
    logout,
    hasPermission,
  } = useAuth();
  const { t } = useSettings();
  const deviceUx = useDeviceUxProfile();
  const isIphoneTouch = deviceUx.mode === "iphone-touch";
  const effectiveUserScopeKey = getEffectiveUserScopeKey(
    user,
    actualUser,
    viewAsUserId,
    isViewingAs,
  );

  const noteSaveStatus = useNoteSaveStatus();

  // A failed mirror of server-loaded data is not itself unsaved user work.
  // Queued/uncertain writes and HTTP drafts remain independent of cache success.
  // A conflict from a failed read, without retained work, is not a dirty edit.
  const hasUnsavedChanges =
    syncState.syncStatus === "pending" ||
    syncState.syncStatus === "syncing" ||
    Boolean(syncState.pendingOperations?.length) ||
    noteSaveStatus !== "idle" ||
    Boolean(hasLocalChanges);

  React.useEffect(() => {
    if (!hasUnsavedChanges) return;
    const guardUnsavedWork = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", guardUnsavedWork);
    return () => window.removeEventListener("beforeunload", guardUnsavedWork);
  }, [hasUnsavedChanges]);

  // Initialize undo/redo keyboard shortcuts
  useUndoShortcuts();

  // Menu management with drag-and-drop
  const {
    menuItems,
    draggedMenu,
    dragOverMenu,
    handleMenuDragStart,
    handleMenuDragOver,
    handleMenuDrop,
    handleMenuDragEnd,
    moveMenuItem,
    setDragOverMenu,
  } = useMenuManagement({ user, hasPermission, storageScopeKey: effectiveUserScopeKey });

  const historyInitializedRef = React.useRef(false);

  // Active view state
  const [activeView, setActiveViewState] = useState(() => {
    const normalizedView = normalizeRequestedView(
      getActiveViewFromLocation() || getStoredActiveView(effectiveUserScopeKey) || "dashboard"
    ).view;
    return menuItems.some((item) => item.view === normalizedView)
      ? normalizedView
      : menuItems[0]?.view || normalizedView;
  });
  const [settingsEntryTopTabHint, setSettingsEntryTopTabHint] =
    useState<SettingsEntryTopTab | null>(() => {
      return normalizeRequestedView(
        getActiveViewFromLocation() || getStoredActiveView(effectiveUserScopeKey) || "dashboard"
      ).settingsEntryTopTabHint;
    });
  React.useEffect(() => {
    setAuditView(activeView);
  }, [activeView]);

  // Instrumented view change function for Blender-style undo
  const setActiveView = (newView: string) => {
    const normalizedView = normalizeRequestedView(newView);
    const before = activeView;
    const after = normalizedView.view;

    if (before !== after) {
      closeAllDropdowns();
      setAuditView(after);
      trackAuditEvent({
        eventType: "navigation.view_change",
        category: "navigation",
        view: after,
        metadata: { before, after },
      });
      captureOperation(
        "view-change",
        "navigation",
        { activeView: before },
        { activeView: after },
        `Switch to ${after} view`,
        "AppNavigation"
      );
    }

    setSettingsEntryTopTabHint(normalizedView.settingsEntryTopTabHint);
    setActiveViewState(after);
  };

  // Modal states
  const [showSearch, setShowSearch] = useState(false);
  const [showExportImport, setShowExportImport] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ProjectNode | null>(null);
  const [pendingTaskNodeId, setPendingTaskNodeId] = useState<string | null>(null);
  const [pendingDocsOpenRequest, setPendingDocsOpenRequest] = useState<PendingDocsOpenRequest | null>(() => (
    getInitialDocsOpenRequest()
  ));
  const [selfServiceTourRequest, setSelfServiceTourRequest] = useState<{
    token: number;
    initialStepId: SelfServiceGuidedTourStepId | null;
  }>({ token: 0, initialStepId: null });

  const requestDocsOpen = React.useCallback((
    target: string | ProjectNode,
    delayMs = 0,
  ): PendingDocsOpenRequest => {
    const targetNode = typeof target === 'string' ? null : target;
    const targetRecord = targetNode as (
      ProjectNode & {
        originalId?: string;
        original_id?: string;
        parent_id?: string | null;
        project_id?: string | null;
      }
    ) | null;
    const targetNodeId = typeof target === 'string' ? target : target.id;
    const rawNodeId = getNodeId(targetNodeId);
    const node = targetNode || projectData?.nodes?.find((candidate) => {
      const candidateRecord = candidate as ProjectNode & { originalId?: string; original_id?: string };
      return (
        candidate.id === targetNodeId ||
        getNodeId(candidate.id) === rawNodeId ||
        candidateRecord.originalId === rawNodeId ||
        candidateRecord.original_id === rawNodeId ||
        (targetRecord?.originalId ? getNodeId(candidate.id) === getNodeId(targetRecord.originalId) : false) ||
        (targetRecord?.original_id ? getNodeId(candidate.id) === getNodeId(targetRecord.original_id) : false)
      );
    }) || null;
    const candidateNodeIds = Array.from(new Set(
      [
        targetNodeId,
        rawNodeId,
        targetRecord?.originalId,
        targetRecord?.original_id,
        node?.id,
        node ? getNodeId(node.id) : undefined,
      ].filter((value): value is string => typeof value === 'string' && value.length > 0)
    ));
    const request: PendingDocsOpenRequest = {
      nodeId: rawNodeId,
      projectId: node?.projectId || targetRecord?.project_id || getProjectIdFromNodeId(targetNodeId, activeProjectId || projectData?.projectId),
      candidateNodeIds,
      title: node?.title,
      type: node?.type,
      parentId: node?.parentId || targetRecord?.parent_id || undefined,
      nodeSnapshot: node || undefined,
    };
    setPendingDocsOpenRequest(request);
    dispatchDocsOpenDocumentRequest(request, delayMs);
    return request;
  }, [activeProjectId, projectData]);

  const handleCloseSearch = React.useCallback(() => {
    setShowSearch(false);
    window.requestAnimationFrame(() => {
      document.getElementById(APP_SEARCH_TRIGGER_ID)?.focus();
    });
  }, []);

  // Register ESC handlers for each layer using centralized keyboard manager.
  // The task (250) and note (260) detail panels register their own Escape
  // entries that protect a focused title draft and pending progress; this
  // fallback stays below them so it only acts before the panel has registered.
  useModalEscape("app-selected-task", () => setSelectedTask(null), {
    priority: 240,
    enabled: !!selectedTask,
  });

  useModalEscape("app-search", handleCloseSearch, {
    priority: 200,
    enabled: showSearch && !selectedTask,
  });

  useModalEscape("app-export-import", () => setShowExportImport(false), {
    priority: 200,
    enabled: showExportImport && !selectedTask && !showSearch,
  });

  // Keyboard shortcuts
  const { isSearchOpen, setIsSearchOpen } = useKeyboardShortcuts({
    // SAI owns search around its Projects frame.
    hasSearchPermission: hasPermission("system", "search") && !isSaiEmbedded,
  });

  // Update search state when keyboard shortcut is used
  React.useEffect(() => {
    if (isSearchOpen && hasPermission("system", "search")) {
      setShowSearch(true);
    }
  }, [isSearchOpen, hasPermission]);

  // Update keyboard shortcut state when modal is closed
  React.useEffect(() => {
    if (!showSearch) {
      setIsSearchOpen(false);
    }
  }, [showSearch, setIsSearchOpen]);

  React.useEffect(() => {
    try {
      localStorage.setItem(getActiveViewStorageKey(effectiveUserScopeKey), activeView);
    } catch {
      // Ignore storage failures; view state should still work in-memory
    }

    if (!historyInitializedRef.current) {
      writeActiveViewToHistory(activeView, "replace");
      historyInitializedRef.current = true;
      return;
    }

    const locationView = normalizeRequestedView(getActiveViewFromLocation()).view;
    if (locationView !== activeView) {
      writeActiveViewToHistory(activeView, "push");
    }
  }, [activeView, effectiveUserScopeKey]);

  React.useEffect(() => {
    const handlePopState = () => {
      const normalizedView = normalizeRequestedView(getActiveViewFromLocation());
      const resolvedView = resolveAccessibleActiveView(normalizedView.view, menuItems);
      if (!resolvedView) {
        return;
      }

      if (resolvedView !== normalizedView.view) {
        writeActiveViewToHistory(resolvedView, "replace");
      }

      let settingsHint = normalizedView.settingsEntryTopTabHint;
      if (resolvedView === "settings" && !settingsHint) {
        const settingsTab = new URLSearchParams(window.location.search).get(APP_SETTINGS_TAB_QUERY_KEY);
        settingsHint = settingsTab === "agents"
          ? "ai"
          : settingsTab && ["general", "users", "projects", "mcp-monitoring", "audit-monitor", "guidelines", "feedback-mode", "ai"].includes(settingsTab)
            ? settingsTab as SettingsEntryTopTab
            : "general";
      }
      setSettingsEntryTopTabHint(resolvedView === "settings" ? settingsHint : null);
      setActiveViewState((currentView) =>
        currentView === resolvedView ? currentView : resolvedView
      );
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [menuItems]);

  React.useEffect(() => {
    const handleAppNavigate = (event: Event) => {
      const detail = (event as CustomEvent<{ view?: string }>).detail;
      if (!detail?.view) {
        return;
      }
      setActiveView(detail.view);
    };

    window.addEventListener('app:navigate', handleAppNavigate as EventListener);
    return () => {
      window.removeEventListener('app:navigate', handleAppNavigate as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  React.useEffect(() => {
    closeAllDropdowns();
  }, [activeView]);

  React.useLayoutEffect(() => {
    if (activeView !== 'docs') {
      return;
    }

    const queuedRequest = getInitialDocsOpenRequest();
    if (!queuedRequest) {
      return;
    }

    setPendingDocsOpenRequest((currentRequest) => {
      if (
        currentRequest?.nodeId === queuedRequest.nodeId &&
        currentRequest?.projectId === queuedRequest.projectId
      ) {
        return currentRequest;
      }
      return queuedRequest;
    });
  }, [activeView]);

  React.useEffect(() => {
    if (activeView !== 'docs' || !pendingDocsOpenRequest?.nodeId) {
      return;
    }

    dispatchDocsOpenDocumentRequest(pendingDocsOpenRequest, 0);
  }, [activeView, pendingDocsOpenRequest]);

  React.useEffect(() => {
    const resolvedView = resolveAccessibleActiveView(activeView, menuItems);
    if (!resolvedView || resolvedView === activeView) {
      return;
    }

    setActiveViewState(resolvedView);
  }, [activeView, menuItems]);

  React.useEffect(() => {
    const handleHistorySetActiveView = (event: Event) => {
      const requestedView = (event as CustomEvent<{ view?: string }>).detail?.view;
      const normalizedView = normalizeRequestedView(requestedView);

      if (!requestedView || !menuItems.some((item) => item.view === normalizedView.view)) {
        return;
      }

      setSettingsEntryTopTabHint(normalizedView.settingsEntryTopTabHint);
      setActiveViewState((currentView) => (
        currentView === normalizedView.view ? currentView : normalizedView.view
      ));
    };

    window.addEventListener('project-data:set-active-view', handleHistorySetActiveView as EventListener);

    return () => {
      window.removeEventListener('project-data:set-active-view', handleHistorySetActiveView as EventListener);
    };
  }, [menuItems]);

  React.useEffect(() => {
    const getNodeById = (nodeId?: string | null) => {
      if (!nodeId) {
        return null;
      }

      return projectData?.nodes?.find((node) => node.id === nodeId) || null;
    };

    const handleOpenTaskDetails = (event: Event) => {
      const nodeId = (event as CustomEvent<{ nodeId?: string }>).detail?.nodeId;
      const node = getNodeById(nodeId);

      if (node) {
        setSelectedTask(node);
        setPendingTaskNodeId(null);
      } else if (nodeId) {
        setPendingTaskNodeId(nodeId);
      }
    };

    const handleOpenActivityDetails = (event: Event) => {
      const activityId = (event as CustomEvent<{ activityId?: string }>).detail?.activityId;
      const node = getNodeById(activityId);

      if (node) {
        setSelectedTask(node);
        setPendingTaskNodeId(null);
      } else if (activityId) {
        setPendingTaskNodeId(activityId);
      }
    };

    const handleDashboardNavigate = (event: Event) => {
      const detail = (event as CustomEvent<{ view?: string; nodeId?: string }>).detail;
      const targetView = detail?.view;
      const nodeId = detail?.nodeId;
      const normalizedTargetView = normalizeRequestedView(targetView);
      const targetViewName = targetView ? normalizedTargetView.view : activeView;

      if (targetView && menuItems.some((item) => item.view === normalizedTargetView.view)) {
        setActiveView(targetView);
      }

      if (nodeId) {
        if (targetViewName === 'docs') {
          requestDocsOpen(nodeId, targetView ? 100 : 0);
          setSelectedTask(null);
          setPendingTaskNodeId(null);
          return;
        }

        window.setTimeout(() => {
          const node = getNodeById(nodeId);
          if (node) {
            setSelectedTask(node);
            setPendingTaskNodeId(null);
          } else {
            setPendingTaskNodeId(nodeId);
          }
        }, targetView ? 100 : 0);
      }
    };

    window.addEventListener("openTaskDetails", handleOpenTaskDetails as EventListener);
    window.addEventListener("openActivityDetails", handleOpenActivityDetails as EventListener);
    window.addEventListener("dashboard:navigate", handleDashboardNavigate as EventListener);

    return () => {
      window.removeEventListener("openTaskDetails", handleOpenTaskDetails as EventListener);
      window.removeEventListener("openActivityDetails", handleOpenActivityDetails as EventListener);
      window.removeEventListener("dashboard:navigate", handleDashboardNavigate as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, menuItems, projectData, requestDocsOpen]);

  React.useEffect(() => {
    if (!pendingTaskNodeId || !projectData?.nodes?.length) {
      return;
    }

    const matchingNode = projectData.nodes.find((node) => node.id === pendingTaskNodeId) || null;
    if (!matchingNode) {
      return;
    }

    setSelectedTask(matchingNode);
    setPendingTaskNodeId(null);
  }, [pendingTaskNodeId, projectData]);

  const handleOpenNotificationTarget = async (notification: InboxNotification) => {
    if (!notification?.project_id) {
      return;
    }

    // Add the target project like a search hop does; a notification must not discard the user's
    // other selected projects (and their persisted selection).
    setActiveProject(notification.project_id);
    try {
      await selectProjects([notification.project_id], 'add');
    } finally {
      setActiveProject(notification.project_id);
    }

    if (notification.node_type === 'document' && notification.node_id) {
      // Keep the target in React state and the existing durable navigation
      // request channel so a lazy Docs chunk can consume it after mounting.
      const request = { projectId: notification.project_id, nodeId: notification.node_id };
      setPendingDocsOpenRequest(request);
      dispatchDocsOpenDocumentRequest(request);
      setActiveView('docs');
      return;
    }

    setActiveView('gantt');
    if (notification.node_id) {
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('dashboard:navigate', {
          detail: {
            view: 'gantt',
            nodeId: notification.node_id,
          },
        }));
      }, 150);
    }
  };

  const activeMenuItem = React.useMemo(
    () => menuItems.find((item) => item.view === activeView),
    [activeView, menuItems]
  );
  const renderMenuItem = activeMenuItem || menuItems[0];
  const activeViewLabel = React.useMemo(
    () => (renderMenuItem ? t(renderMenuItem.label) : t("Workspace")),
    [renderMenuItem, t]
  );
  const workspaceLabel = React.useMemo(() => {
    if (visibleProjects.length === 0) {
      return t("No project selected");
    }

    if (visibleProjects.length === 1) {
      return visibleProjects[0].projectName;
    }

    return t("{{count}} active projects", { count: visibleProjects.length });
  }, [t, visibleProjects]);
  const workspaceDetail = React.useMemo(() => {
    if (visibleProjects.length === 1) {
      return getProjectCompanyLabel(visibleProjects[0], activeViewLabel);
    }

    return activeViewLabel;
  }, [activeViewLabel, visibleProjects]);
  // A user without projects gets the catalog's synthetic 'default' entry;
  // it is not a project, so it never reaches the Feedback Mode provider.
  const feedbackProjectId = [activeProjectId, visibleProjects[0]?.projectId, projectData?.projectId]
    .find((projectId): projectId is string => Boolean(projectId) && projectId !== 'default') || null;
  const openFeedbackModeSettings = () => {
    setActiveView('settings');
    setSettingsEntryTopTabHint('feedback-mode');
  };
  const feedbackControls = (
    <FeedbackModeHost
      projectId={feedbackProjectId}
      pageKey={`view:${activeView}`}
      routeLabel={activeViewLabel}
      user={user}
      canManageSettings={actualUser?.godMode === true}
      onOpenSettings={openFeedbackModeSettings}
    />
  );
  const onboardingProject = visibleProjects.length === 1 ? visibleProjects[0] : null;
  const onboardingSettings = onboardingProject?.metadata?.settings;
  const onboardingTemplate = readOnboardingTemplate(onboardingSettings);
  const onboardingStatus = readOnboardingStatus(onboardingSettings);
  const isSelfServiceOwner = Boolean(
    user?.permissions?.["self_service.owner"] || user?.selfServicePlan,
  );
  const isSelfServiceTourReady =
    isSelfServiceOwner &&
    Boolean(onboardingProject) &&
    onboardingTemplate === "self_service_guided_v1" &&
    (onboardingStatus === "completed" || onboardingStatus === "skipped");
  const shouldShowSelfServiceSetup =
    isSelfServiceOwner &&
    onboardingProject &&
    onboardingTemplate === "self_service_guided_v1" &&
    (!onboardingStatus || onboardingStatus === "pending");

  const handleSelfServiceSetupComplete = React.useCallback(
    async (preferredStartView: "gantt" | "kanban" | "docs") => {
      if (onboardingProject?.projectId) {
        await refreshProject(onboardingProject.projectId);
      }
      setActiveView(preferredStartView);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onboardingProject?.projectId, refreshProject, activeView],
  );

  const handleSelfServiceSetupSkip = React.useCallback(
    async () => {
      if (onboardingProject?.projectId) {
        await refreshProject(onboardingProject.projectId);
      }
      setActiveView("dashboard");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onboardingProject?.projectId, refreshProject, activeView],
  );

  const handleOpenSelfServiceTour = React.useCallback((initialStepId: SelfServiceGuidedTourStepId = "dashboard") => {
    setSelfServiceTourRequest((current) => ({
      token: current.token + 1,
      initialStepId,
    }));
  }, []);

  const handleOpenPricing = React.useCallback(() => {
    navigate("/pricing");
  }, [navigate]);

  const handleSelfServiceTourNavigate = React.useCallback(
    (view: string) => {
      const normalizedView = normalizeRequestedView(view).view;
      if (menuItems.some((item) => item.view === normalizedView)) {
        setActiveView(view);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menuItems, activeView],
  );

  if (shouldShowSelfServiceSetup && onboardingProject) {
    return (
      <SelfServiceSetupWizard
        project={onboardingProject}
        onComplete={handleSelfServiceSetupComplete}
        onSkip={handleSelfServiceSetupSkip}
      />
    );
  }

  return (
    <div
      data-app-ready="true"
      data-active-view={activeView}
      className={cn("h-screen grid transition-colors", theme.bgPrimary)}
      data-device-ux-mode={deviceUx.mode}
      style={{
        gridTemplateRows: isIphoneTouch ? "auto 1fr auto" : "auto auto 1fr",
        width: isIphoneTouch ? "100svw" : undefined,
        maxWidth: isIphoneTouch ? "100svw" : undefined,
        overflowX: isIphoneTouch ? "hidden" : undefined,
      }}
    >
      {/* Header */}
      <AppHeader
        theme={theme}
        user={user}
        actualUser={actualUser}
        viewAsUserId={viewAsUserId}
        viewAsOptions={viewAsOptions}
        isViewingAs={isViewingAs}
        activeViewLabel={activeViewLabel}
        workspaceLabel={workspaceLabel}
        workspaceDetail={workspaceDetail}
        hasUnsavedChanges={hasUnsavedChanges}
        isDarkMode={isDarkMode}
        hasPermission={hasPermission}
        feedbackControls={feedbackControls}
        isSaiEmbedded={isSaiEmbedded}
        canOpenSelfServiceTour={isSelfServiceTourReady}
        onOpenSelfServiceTour={() => handleOpenSelfServiceTour("dashboard")}
        onOpenPricing={handleOpenPricing}
        onOpenImport={() => setShowExportImport(true)}
        toggleDarkMode={toggleDarkMode}
        setShowSearch={setShowSearch}
        onOpenNotificationTarget={handleOpenNotificationTarget}
        setViewAsUserId={setViewAsUserId}
        updateSelfServicePlan={updateSelfServicePlan}
        logout={logout}
      />

      {/* Navigation */}
      <div style={{ gridRow: isIphoneTouch ? 3 : undefined }}>
        <AppNavigation
          theme={theme}
          menuItems={menuItems}
          activeView={activeView}
          draggedMenu={draggedMenu}
          dragOverMenu={dragOverMenu}
          setActiveView={setActiveView}
          setDragOverMenu={setDragOverMenu}
          handleMenuDragStart={handleMenuDragStart}
          handleMenuDragOver={handleMenuDragOver}
          handleMenuDrop={handleMenuDrop}
          handleMenuDragEnd={handleMenuDragEnd}
          moveMenuItem={moveMenuItem}
          isIphoneTouch={isIphoneTouch}
        />
      </div>

      <SelfServiceGuidedTour
        isEligible={isSelfServiceTourReady}
        userId={user?.id || null}
        projectId={onboardingProject?.projectId || null}
        onboardingTemplate={onboardingTemplate}
        onboardingStatus={onboardingStatus}
        activeView={activeView}
        openRequestToken={selfServiceTourRequest.token}
        initialStepId={selfServiceTourRequest.initialStepId}
        autoOpen={!isSaiEmbedded && onboardingStatus === "completed"}
        onNavigateToView={handleSelfServiceTourNavigate}
      />

      {/* Main Content */}
      <main
        data-view-probe-version="1"
        className={cn(
          "min-w-0 min-h-0 overflow-auto overflow-x-hidden",
          isIphoneTouch && "overscroll-contain",
        )}
        style={{ gridRow: isIphoneTouch ? 2 : undefined }}
      >
        {menuItems.length === 0 ? (
          <NoAccessibleWorkspace
            onReload={() => window.location.reload()}
            onSignOut={logout}
          />
        ) : (
          renderActiveView(renderMenuItem, theme, {
            settingsEntryTopTabHint,
            docsOpenRequest: pendingDocsOpenRequest,
            onDocsOpenRequestConsumed: () => setPendingDocsOpenRequest(null),
            onOpenSelfServiceTour: handleOpenSelfServiceTour,
          })
        )}
        <SearchPanel
          isOpen={showSearch}
          onClose={handleCloseSearch}
          onSelectTask={(taskId: string) => {
            const task = projectData?.nodes?.find((node) => node.id === taskId);
            if (task) {
              setSelectedTask(task);
            }
          }}
          onNavigateToView={(viewName: string, taskId?: string, node?: ProjectNode) => {
            if (taskId) {
              const normalizedView = normalizeRequestedView(viewName);
              if (normalizedView.view === 'docs') {
                const docsOpenRequest = requestDocsOpen(node || taskId);
                const targetProjectId = docsOpenRequest.projectId;

                void (async () => {
                  if (targetProjectId && targetProjectId !== 'default') {
                    setActiveProject(targetProjectId);
                    try {
                      await selectProjects([targetProjectId], 'add');
                    } finally {
                      setActiveProject(targetProjectId);
                    }
                  }
                  setActiveView(viewName);
                  setSelectedTask(null);
                  setPendingTaskNodeId(null);
                })();
                return;
              }

              setActiveView(viewName);
              setTimeout(() => {
                const task = projectData?.nodes?.find(
                  (node) => node.id === taskId
                );
                if (task) {
                  setSelectedTask(task);
                }
              }, 100);
              return;
            }

            setActiveView(viewName);
          }}
        />
      </main>

      {/* Global Task Details Panel */}
      {selectedTask && (
        <TaskDetailsPanel
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onUndo={() => {
            if (selectedTask?.id) {
              const originalTask = projectData?.nodes?.find(
                (n) => n.id === selectedTask.id
              );
              if (originalTask) setSelectedTask(originalTask);
            }
          }}
        />
      )}

      {/* Export/Import Modal */}
      <ImportExportModal
        isOpen={showExportImport}
        onClose={() => setShowExportImport(false)}
      />

      <ProjectFilterDropdownHost />

      {/* Blender-style Undo History Panel */}
      <UndoHistoryPanel />

    </div>
  );
};

const App: React.FC = () => {
  return (
    <AsyncErrorBoundary>
      <Router basename={ROUTER_BASENAME}>
        <ThemeProvider>
          <AuthProvider>
            <AppWithTheme />
          </AuthProvider>
        </ThemeProvider>
      </Router>
    </AsyncErrorBoundary>
  );
};

const AppWithTheme: React.FC = () => {
  const { theme } = useTheme();

  return (
    <SettingsProvider theme={theme}>
      <Routes>
        <Route path="/print/:slug" element={<PrintPreviewPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route
          path="/iphone-planning-board"
          element={
            <React.Suspense
              fallback={
                <div className="flex h-screen items-center justify-center bg-[#050608] text-sm font-semibold text-zinc-300">
                  {i18n.t("Loading mobile planning board...")}
                </div>
              }
            >
              <MobilePlanningBoardPrototype />
            </React.Suspense>
          }
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/native/sai/authorize" element={<ProtectedRoute><React.Suspense fallback={null}><SaiNativeConsent /></React.Suspense></ProtectedRoute>} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/legal/refund-policy" element={<LegalPolicyPage />} />
        <Route path="/billing/setup/*" element={<BillingSetupPage />} />
        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route
          path="/projects/agent-office"
          element={
            <ProtectedRoute resource="agent_office" action="access">
              <AgentOfficeEmbedPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/agent-office"
          element={
            <ProtectedRoute resource="agent_office" action="access">
              <AgentOfficeEmbedPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <EffectiveUserScopedWorkspace />
            </ProtectedRoute>
          }
        />
      </Routes>
    </SettingsProvider>
  );
};

const EffectiveUserScopedWorkspace: React.FC = () => {
  const {
    user,
    actualUser,
    viewAsUserId,
    isViewingAs,
    clearViewAs,
  } = useAuth();
  const effectiveUserScopeKey = getEffectiveUserScopeKey(
    user,
    actualUser,
    viewAsUserId,
    isViewingAs,
  );
  const isViewAsTargetLoading = Boolean(
    isViewingAs && viewAsUserId && user?.id !== viewAsUserId,
  );

  if (isViewAsTargetLoading) {
    return (
      <SessionRecoveryScreen
        onReload={() => window.location.reload()}
        onReturnToAccount={clearViewAs}
      />
    );
  }

  return (
    <React.Fragment key={`${effectiveUserScopeKey}:user:${user?.id || "anonymous"}`}>
      <MultiProjectProvider>
        <ProjectDataProvider>
          <DashboardLayoutProvider>
            <AppContent />
          </DashboardLayoutProvider>
        </ProjectDataProvider>
      </MultiProjectProvider>
    </React.Fragment>
  );
};

export default App;
